import React, { useEffect, useState } from 'react';
import Sidebar from '@/components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Send, Mail, Users, Filter, History, CheckCircle, XCircle, FileText, Eye } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export default function Announcements() {
  const [recipientCounts, setRecipientCounts] = useState(null);
  const [templates, setTemplates] = useState({});
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [allFamilies, setAllFamilies] = useState([]);
  const [selectedFamilyIds, setSelectedFamilyIds] = useState([]);
  const [familySearchTerm, setFamilySearchTerm] = useState('');
  
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    templateId: '',
    sendTo: 'all',
    locationFilter: '',
    wardFilter: '',
    statusFilter: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [countsRes, historyRes, templatesRes, familiesRes] = await Promise.all([
        api.get('/announcements/recipient-counts'),
        api.get('/announcements/history'),
        api.get('/announcements/templates'),
        api.get('/families')
      ]);
      setRecipientCounts(countsRes.data);
      setHistory(historyRes.data);
      setTemplates(templatesRes.data);
      setAllFamilies(familiesRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const getEstimatedRecipients = () => {
    if (!recipientCounts) return 0;
    
    if (formData.sendTo === 'all') {
      return recipientCounts.total;
    }
    
    if (formData.sendTo === 'specific') {
      return selectedFamilyIds.length;
    }
    
    if (formData.locationFilter && formData.locationFilter !== 'any') {
      return recipientCounts.by_location[formData.locationFilter] || 0;
    }
    if (formData.wardFilter && formData.wardFilter !== 'any') {
      return recipientCounts.by_ward[formData.wardFilter] || 0;
    }
    if (formData.statusFilter && formData.statusFilter !== 'any') {
      return recipientCounts.by_delivery_status[formData.statusFilter] || 0;
    }
    
    return recipientCounts.total;
  };

  const toggleFamilySelection = (familyId) => {
    setSelectedFamilyIds(prev => 
      prev.includes(familyId)
        ? prev.filter(id => id !== familyId)
        : [...prev, familyId]
    );
  };

  const filteredFamilies = allFamilies.filter(family => {
    if (!familySearchTerm) return true;
    const searchLower = familySearchTerm.toLowerCase();
    const fullName = `${family.parent_first_name} ${family.parent_last_name}`.toLowerCase();
    return fullName.includes(searchLower) || family.email.toLowerCase().includes(searchLower);
  });

  const handleTemplateChange = (templateId) => {
    if (templateId === 'custom') {
      setFormData({ ...formData, templateId: '', subject: '', message: '' });
    } else if (templates[templateId]) {
      setFormData({
        ...formData,
        templateId,
        subject: templates[templateId].subject,
        message: templateId === 'general_announcement' ? formData.message : ''
      });
    }
  };

  const handlePreview = async () => {
    try {
      const templateId = formData.templateId || 'general_announcement';
      const response = await api.post(`/announcements/preview-template?template_id=${templateId}`, {
        parent_name: "Sample Family",
        custom_message: formData.message || "Your custom message will appear here."
      });
      setPreviewHtml(response.data.html_preview);
      setShowPreview(true);
    } catch (error) {
      toast.error('Failed to load preview');
    }
  };

  const handleSendAnnouncement = async (e) => {
    e.preventDefault();
    
    if (!formData.templateId && !formData.message.trim()) {
      toast.error('Please select a template or write a custom message');
      return;
    }
    
    if (formData.sendTo === 'specific' && selectedFamilyIds.length === 0) {
      toast.error('Please select at least one family');
      return;
    }
    
    const filters = {};
    if (formData.sendTo === 'filtered') {
      if (formData.locationFilter && formData.locationFilter !== 'any') filters.location_type = formData.locationFilter;
      if (formData.wardFilter && formData.wardFilter !== 'any') filters.ward_number = formData.wardFilter;
      if (formData.statusFilter && formData.statusFilter !== 'any') filters.delivery_status = formData.statusFilter;
    }
    
    setSending(true);
    try {
      const response = await api.post('/announcements/send', {
        subject: formData.subject || null,
        message: formData.message || null,
        template_id: formData.templateId || null,
        send_to: formData.sendTo,
        filters: Object.keys(filters).length > 0 ? filters : null,
        family_ids: formData.sendTo === 'specific' ? selectedFamilyIds : null
      });
      
      const { emails_sent, emails_failed, total_recipients } = response.data;
      
      if (emails_sent > 0) {
        toast.success(`Announcement sent to ${emails_sent} of ${total_recipients} families!`);
      } else if (emails_failed > 0) {
        toast.warning(`Email sending failed. Please check your configuration.`);
      } else {
        toast.info('No emails were sent.');
      }
      
      // Reset form and refresh history
      setFormData({
        subject: '',
        message: '',
        templateId: '',
        sendTo: 'all',
        locationFilter: '',
        wardFilter: '',
        statusFilter: ''
      });
      setSelectedFamilyIds([]);
      setFamilySearchTerm('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send announcement');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex">
      <Sidebar />
      <div className="ml-64 flex-1 p-8" data-testid="announcements-page">
        <div className="max-w-5xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-foreground mb-2">Announcements</h1>
            <p className="text-stone-600">Send email notifications to registered families</p>
          </div>

          <Tabs defaultValue="compose" className="space-y-6">
            <TabsList>
              <TabsTrigger value="compose" data-testid="compose-tab">
                <Mail className="w-4 h-4 mr-2" />
                Compose
              </TabsTrigger>
              <TabsTrigger value="templates" data-testid="templates-tab">
                <FileText className="w-4 h-4 mr-2" />
                Templates
              </TabsTrigger>
              <TabsTrigger value="history" data-testid="history-tab">
                <History className="w-4 h-4 mr-2" />
                History
              </TabsTrigger>
            </TabsList>

            <TabsContent value="compose">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Compose Form */}
                <div className="lg:col-span-2">
                  <Card className="border-stone-200 shadow-card">
                    <CardHeader>
                      <CardTitle>Compose Announcement</CardTitle>
                      <CardDescription>Choose a template or write a custom message</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <form onSubmit={handleSendAnnouncement} className="space-y-4">
                        {/* Template Selection */}
                        <div>
                          <Label htmlFor="template">Email Template</Label>
                          <Select
                            value={formData.templateId || 'custom'}
                            onValueChange={handleTemplateChange}
                          >
                            <SelectTrigger data-testid="template-select">
                              <SelectValue placeholder="Select a template" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="custom">✏️ Custom Message</SelectItem>
                              {Object.entries(templates)
                                .filter(([id]) => id !== 'migration_welcome')
                                .map(([id, template]) => (
                                <SelectItem key={id} value={id}>
                                  {template.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {formData.templateId && templates[formData.templateId] && (
                            <p className="text-sm text-stone-500 mt-1">
                              {templates[formData.templateId].description}
                            </p>
                          )}
                        </div>

                        <div>
                          <Label htmlFor="subject">Subject</Label>
                          <Input
                            id="subject"
                            data-testid="subject-input"
                            value={formData.subject}
                            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                            placeholder={formData.templateId ? "Using template subject" : "e.g., Important Update"}
                          />
                        </div>
                        
                        {(!formData.templateId || formData.templateId === 'general_announcement') && (
                          <div>
                            <Label htmlFor="message">Message *</Label>
                            <Textarea
                              id="message"
                              data-testid="message-input"
                              value={formData.message}
                              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                              placeholder="Write your announcement message here..."
                              rows={6}
                            />
                          </div>
                        )}

                        {formData.templateId && formData.templateId !== 'general_announcement' && (
                          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                            <p className="text-sm text-green-800">
                              <strong>Using template:</strong> {templates[formData.templateId]?.name}
                            </p>
                            <p className="text-sm text-green-700 mt-1">
                              Each recipient will receive a personalized email with their name.
                            </p>
                          </div>
                        )}

                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handlePreview}
                            className="flex-1"
                            data-testid="preview-button"
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            Preview Email
                          </Button>
                        </div>

                        <div className="pt-4 border-t">
                          <Label className="mb-3 block">Recipients</Label>
                          <div className="space-y-3">
                            <div className="flex items-center gap-4">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name="sendTo"
                                  value="all"
                                  checked={formData.sendTo === 'all'}
                                  onChange={(e) => setFormData({ ...formData, sendTo: e.target.value })}
                                  className="w-4 h-4"
                                />
                                <span>All Families</span>
                                {recipientCounts && (
                                  <Badge variant="secondary">{recipientCounts.total}</Badge>
                                )}
                              </label>
                            </div>
                            <div className="flex items-center gap-4">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name="sendTo"
                                  value="specific"
                                  checked={formData.sendTo === 'specific'}
                                  onChange={(e) => {
                                    setFormData({ ...formData, sendTo: e.target.value });
                                    setSelectedFamilyIds([]);
                                  }}
                                  className="w-4 h-4"
                                />
                                <span>Select Specific Families</span>
                                {selectedFamilyIds.length > 0 && (
                                  <Badge variant="secondary">{selectedFamilyIds.length} selected</Badge>
                                )}
                              </label>
                            </div>
                            <div className="flex items-center gap-4">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name="sendTo"
                                  value="filtered"
                                  checked={formData.sendTo === 'filtered'}
                                  onChange={(e) => setFormData({ ...formData, sendTo: e.target.value })}
                                  className="w-4 h-4"
                                />
                                <span>Filter by Location/Status</span>
                              </label>
                            </div>
                          </div>
                        </div>

                        {formData.sendTo === 'specific' && (
                          <div className="p-4 bg-stone-50 rounded-lg space-y-4">
                            <div className="flex items-center gap-2 text-sm text-stone-600 mb-2">
                              <Users className="w-4 h-4" />
                              <span>Select Families by Name</span>
                            </div>
                            
                            <Input
                              placeholder="Search families by name or email..."
                              value={familySearchTerm}
                              onChange={(e) => setFamilySearchTerm(e.target.value)}
                              className="mb-3"
                              data-testid="family-search-input"
                            />
                            
                            {selectedFamilyIds.length > 0 && (
                              <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded-lg">
                                <p className="text-sm text-green-800 font-medium mb-2">
                                  Selected ({selectedFamilyIds.length}):
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  {selectedFamilyIds.map(id => {
                                    const family = allFamilies.find(f => f.id === id);
                                    if (!family) return null;
                                    return (
                                      <Badge 
                                        key={id} 
                                        variant="secondary" 
                                        className="cursor-pointer hover:bg-red-100"
                                        onClick={() => toggleFamilySelection(id)}
                                      >
                                        {family.parent_first_name} {family.parent_last_name} ✕
                                      </Badge>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            
                            <div className="max-h-60 overflow-y-auto border rounded-lg bg-white">
                              {filteredFamilies.length === 0 ? (
                                <div className="p-4 text-center text-stone-500">
                                  {familySearchTerm ? 'No families match your search' : 'No families available'}
                                </div>
                              ) : (
                                filteredFamilies.map(family => (
                                  <div 
                                    key={family.id}
                                    className={`p-3 border-b last:border-b-0 flex items-center gap-3 cursor-pointer hover:bg-stone-50 transition-colors ${
                                      selectedFamilyIds.includes(family.id) ? 'bg-green-50' : ''
                                    }`}
                                    onClick={() => toggleFamilySelection(family.id)}
                                    data-testid={`family-option-${family.id}`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedFamilyIds.includes(family.id)}
                                      onChange={() => {}}
                                      className="w-4 h-4"
                                    />
                                    <div className="flex-1">
                                      <p className="font-medium">{family.parent_first_name} {family.parent_last_name}</p>
                                      <p className="text-sm text-stone-500">{family.email}</p>
                                    </div>
                                    <Badge variant="outline" className="text-xs">
                                      {family.location_type === 'DC' ? `Ward ${family.ward_number || '?'}` : family.location_type}
                                    </Badge>
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                        )}

                        {formData.sendTo === 'filtered' && (
                          <div className="p-4 bg-stone-50 rounded-lg space-y-4">
                            <div className="flex items-center gap-2 text-sm text-stone-600 mb-2">
                              <Filter className="w-4 h-4" />
                              <span>Filter Options</span>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <Label htmlFor="locationFilter">Location</Label>
                                <Select
                                  value={formData.locationFilter || "any"}
                                  onValueChange={(value) => setFormData({ ...formData, locationFilter: value })}
                                >
                                  <SelectTrigger data-testid="location-filter">
                                    <SelectValue placeholder="Any location" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="any">Any location</SelectItem>
                                    <SelectItem value="DC">DC ({recipientCounts?.by_location?.DC || 0})</SelectItem>
                                    <SelectItem value="Maryland">Maryland ({recipientCounts?.by_location?.Maryland || 0})</SelectItem>
                                    <SelectItem value="Virginia">Virginia ({recipientCounts?.by_location?.Virginia || 0})</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              
                              <div>
                                <Label htmlFor="wardFilter">Ward (DC only)</Label>
                                <Select
                                  value={formData.wardFilter || "any"}
                                  onValueChange={(value) => setFormData({ ...formData, wardFilter: value })}
                                >
                                  <SelectTrigger data-testid="ward-filter">
                                    <SelectValue placeholder="Any ward" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="any">Any ward</SelectItem>
                                    {recipientCounts?.by_ward && Object.entries(recipientCounts.by_ward).map(([ward, count]) => (
                                      <SelectItem key={ward} value={ward}>Ward {ward} ({count})</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              
                              <div>
                                <Label htmlFor="statusFilter">Delivery Status</Label>
                                <Select
                                  value={formData.statusFilter || "any"}
                                  onValueChange={(value) => setFormData({ ...formData, statusFilter: value })}
                                >
                                  <SelectTrigger data-testid="status-filter">
                                    <SelectValue placeholder="Any status" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="any">Any status</SelectItem>
                                    <SelectItem value="pending">Pending ({recipientCounts?.by_delivery_status?.pending || 0})</SelectItem>
                                    <SelectItem value="delivered">Delivered ({recipientCounts?.by_delivery_status?.delivered || 0})</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="flex items-center justify-between pt-4">
                          <div className="flex items-center gap-2 text-sm text-stone-600">
                            <Users className="w-4 h-4" />
                            <span>Estimated recipients: <strong>{getEstimatedRecipients()}</strong></span>
                          </div>
                          <Button
                            type="submit"
                            disabled={sending || (!formData.templateId && !formData.message)}
                            className="rounded-full"
                            data-testid="send-announcement-button"
                          >
                            <Send className="w-4 h-4 mr-2" />
                            {sending ? 'Sending...' : 'Send Announcement'}
                          </Button>
                        </div>
                      </form>
                    </CardContent>
                  </Card>
                </div>

                {/* Stats Sidebar */}
                <div className="space-y-4">
                  <Card className="border-stone-200 shadow-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">Recipient Stats</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {loading ? (
                        <p className="text-sm text-stone-500">Loading...</p>
                      ) : recipientCounts ? (
                        <div className="space-y-4">
                          <div>
                            <p className="text-3xl font-bold text-primary">{recipientCounts.total}</p>
                            <p className="text-sm text-stone-600">Total Families</p>
                          </div>
                          <div className="pt-3 border-t">
                            <p className="text-sm font-medium mb-2">By Location</p>
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span>DC</span>
                                <span>{recipientCounts.by_location.DC}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Maryland</span>
                                <span>{recipientCounts.by_location.Maryland}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Virginia</span>
                                <span>{recipientCounts.by_location.Virginia}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="templates">
              <Card className="border-stone-200 shadow-card">
                <CardHeader>
                  <CardTitle>Email Templates</CardTitle>
                  <CardDescription>Pre-built templates for common notifications</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="templates-list">
                    {Object.entries(templates).map(([id, template]) => (
                      <Card key={id} className="border-stone-200">
                        <CardContent className="pt-4">
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="font-semibold">{template.name}</h4>
                            <Badge variant="outline">{id}</Badge>
                          </div>
                          <p className="text-sm text-stone-600 mb-3">{template.description}</p>
                          <p className="text-xs text-stone-500 mb-3">
                            <strong>Subject:</strong> {template.subject}
                          </p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              handleTemplateChange(id);
                              document.querySelector('[data-testid="compose-tab"]')?.click();
                            }}
                            className="w-full"
                          >
                            Use This Template
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="history">
              <Card className="border-stone-200 shadow-card">
                <CardHeader>
                  <CardTitle>Announcement History</CardTitle>
                  <CardDescription>Previously sent announcements</CardDescription>
                </CardHeader>
                <CardContent>
                  {history.length === 0 ? (
                    <div className="text-center py-8 text-stone-500">
                      <History className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>No announcements sent yet</p>
                    </div>
                  ) : (
                    <div className="space-y-4" data-testid="announcement-history">
                      {history.map((item) => (
                        <div key={item.id} className="p-4 border rounded-lg">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h4 className="font-semibold">{item.subject}</h4>
                              {item.template_name && (
                                <Badge variant="outline" className="mt-1">{item.template_name}</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {item.emails_sent > 0 ? (
                                <Badge className="bg-green-100 text-green-800">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  {item.emails_sent} sent
                                </Badge>
                              ) : (
                                <Badge className="bg-red-100 text-red-800">
                                  <XCircle className="w-3 h-3 mr-1" />
                                  Not sent
                                </Badge>
                              )}
                            </div>
                          </div>
                          {item.message && (
                            <p className="text-sm text-stone-600 mb-2 line-clamp-2">{item.message}</p>
                          )}
                          <div className="flex gap-4 text-xs text-stone-500">
                            <span>
                              {new Date(item.sent_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit'
                              })}
                            </span>
                            <span>Recipients: {item.total_recipients}</span>
                            {item.send_to === 'filtered' && (
                              <span>Filtered</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Email Preview</DialogTitle>
          </DialogHeader>
          <div 
            className="border rounded-lg overflow-hidden"
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
